import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

import { ErrorBoundary } from './components/ErrorBoundary'
import { Shell } from './components/Shell'
import { installAnalytics } from './lib/analytics'
import { useSession } from './lib/session'
import { AdminLabor } from './routes/admin/Labor'
import { AdminOverview } from './routes/admin/Overview'
import { AdminProducts } from './routes/admin/Products'
import { AdminSessions } from './routes/admin/Sessions'
import { AdminStores } from './routes/admin/Stores'
import { Confirmation } from './routes/Confirmation'
import { Availability } from './routes/op/Availability'
import { Bookings } from './routes/op/Bookings'
import { Inbox } from './routes/op/Inbox'
import { RequestDetail } from './routes/op/RequestDetail'
import { EstimateIntake } from './routes/EstimateIntake'
import { Landing } from './routes/Landing'
import { NotFound } from './routes/NotFound'
import { Privacy, Terms } from './routes/Privacy'
import { Schedule } from './routes/Schedule'
import { SharedPreview } from './routes/SharedPreview'
import { RequireStaff } from './routes/RequireStaff'
import { Resume } from './routes/Resume'
import { StaffLogin } from './routes/StaffLogin'
import { Workspace } from './routes/Workspace'
import { WorkspaceOffer } from './routes/WorkspaceOffer'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A homeowner on a phone in a basement should not have three retries' worth of spinner
      // before being told the network is down.
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
})

export function App() {
  const load = useSession((state) => state.load)

  useEffect(() => {
    installAnalytics()
    void load()
  }, [load])

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Shell>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/estimate" element={<EstimateIntake />} />
              <Route path="/estimate/:id/schedule" element={<Schedule />} />
              <Route path="/estimate/:id/booked/:bookingId" element={<Confirmation />} />
              <Route path="/estimate/:id/next" element={<WorkspaceOffer />} />
              <Route path="/resume" element={<Resume />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/w/:sessionId" element={<Workspace />} />
              {/* Public: the only route a stranger with a link can reach. */}
              <Route path="/s/:token" element={<SharedPreview />} />
              <Route path="/staff/login" element={<StaffLogin />} />
              <Route
                path="/op"
                element={
                  <RequireStaff>
                    <Inbox />
                  </RequireStaff>
                }
              />
              <Route
                path="/op/requests/:id"
                element={
                  <RequireStaff>
                    <RequestDetail />
                  </RequireStaff>
                }
              />
              <Route
                path="/op/bookings"
                element={
                  <RequireStaff>
                    <Bookings />
                  </RequireStaff>
                }
              />
              <Route
                path="/op/availability"
                element={
                  <RequireStaff>
                    <Availability />
                  </RequireStaff>
                }
              />
              <Route
                path="/admin"
                element={
                  <RequireStaff role="admin">
                    <AdminOverview />
                  </RequireStaff>
                }
              />
              <Route
                path="/admin/products"
                element={
                  <RequireStaff role="admin">
                    <AdminProducts />
                  </RequireStaff>
                }
              />
              <Route
                path="/admin/stores"
                element={
                  <RequireStaff role="admin">
                    <AdminStores />
                  </RequireStaff>
                }
              />
              <Route
                path="/admin/labor"
                element={
                  <RequireStaff role="admin">
                    <AdminLabor />
                  </RequireStaff>
                }
              />
              <Route
                path="/admin/sessions"
                element={
                  <RequireStaff role="admin">
                    <AdminSessions />
                  </RequireStaff>
                }
              />
              <Route path="/index.html" element={<Navigate to="/" replace />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Shell>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
